import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { useAppServices } from '@/app/AppServicesContext';
import type { CosmosControls } from '@/core/CosmosControls';
import {
  CosmosContext,
  type CosmosState,
  type CosmosStatus,
} from '@/features/cosmos/CosmosContext';

import styles from './CosmosLayer.module.css';

/**
 * Pose le décor cosmique derrière toute la page et le partage avec les sections.
 * Le moteur (Three.js et les shaders) n'est téléchargé que si l'appareil peut
 * le faire tourner ; sinon, chaque section garde sa version fixe.
 */
export function CosmosLayer({ children }: { readonly children: ReactNode }) {
  const { device, motion } = useAppServices();
  const enabled = device.canRenderCosmos;
  const hostRef = useRef<HTMLDivElement>(null);
  const [controls, setControls] = useState<CosmosControls | null>(null);
  const [status, setStatus] = useState<CosmosStatus>(enabled ? 'pending' : 'off');

  useEffect(() => {
    const host = hostRef.current;
    if (!enabled || !host) return;

    const lifetime = new AbortController();
    const disposed = (): boolean => lifetime.signal.aborted;
    let release: (() => void) | null = null;

    const stop = (): void => {
      delete document.documentElement.dataset.cosmosReady;
      release?.();
      release = null;
      setControls(null);
    };
    // Machine trop juste : on libère le moteur et la page reprend sa version fixe.
    const retire = (): void => {
      stop();
      setStatus('off');
    };

    const boot = async (): Promise<void> => {
      const [{ CosmosDirector }, { COSMOS_QUALITY }] = await Promise.all([
        import('@/cosmos/CosmosDirector'),
        import('@/cosmos/CosmosQuality'),
      ]);
      if (disposed()) return;
      const director = new CosmosDirector(host, {
        frames: motion.frames,
        quality: COSMOS_QUALITY[device.gpuTier],
        framing: device.portrait ? 'portrait' : 'landscape',
        root: document,
        onUnsustainable: retire,
      });
      release = () => {
        director.dispose();
      };
      const running = await director.start();
      if (disposed()) return;
      if (!running) {
        retire();
        return;
      }
      document.documentElement.dataset.cosmosReady = '';
      setControls(director);
      setStatus('ready');
    };

    boot().catch((error: unknown) => {
      if (disposed()) return;
      console.error('Décor cosmique indisponible', error);
      retire();
    });

    return () => {
      lifetime.abort();
      stop();
    };
  }, [enabled, device, motion]);

  const state = useMemo<CosmosState>(
    () => ({ status, ready: status === 'ready', controls }),
    [status, controls],
  );

  return (
    <CosmosContext value={state}>
      {enabled && <div ref={hostRef} className={styles.layer} aria-hidden="true" />}
      {children}
    </CosmosContext>
  );
}
